import React, { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import { IconTitle } from '@/components/ui/icon-title';

interface EventType {
  id: string;
  label: string;
  name: string;
  color: string;
  icon?: React.ReactNode;
}

interface GroupedAutomationLog {
  automation_id: number | null;
  automation_name: string;
  profile_id: number | null;
  profile_name: string;
  blueprint_name: string;
  shadow: boolean;
  started_at: string;
  ended_at: string;
  stages: {
    stage: string;
    started_at: string;
    ended_at: string;
    shadow: boolean;
    metadata: any | null;
  }[];
  metadata: any | null;
}

interface EventTimelineProps {
  eventTypes: EventType[];
  profileName?: string;
  showEventTypes?: boolean;
  eventLogData?: GroupedAutomationLog[];
  isLoading?: boolean;
  selectedDate?: dayjs.Dayjs;
}

// Configuration settings for easy adjustment
const CONFIG = {
  timeline: {
    width: 97, // Adjust this value to change the timeline width (percentage)
    height: 8, // Height of the timeline bar in Tailwind height units (h-6 = 1.5rem)
    minWidth: 0, // Minimum width in pixels (0 = no minimum)
    maxWidth: 100, // Maximum width in percentage (100 = full width)
  }
};

// Time data for the timeline - showing every 2 hours
const timelineData = [
  { time: '00:00' }, { time: '02:00' }, { time: '04:00' }, { time: '06:00' },
  { time: '08:00' }, { time: '10:00' }, { time: '12:00' }, { time: '14:00' },
  { time: '16:00' }, { time: '18:00' }, { time: '20:00' }, { time: '22:00' }
];

export const EventTimeline: React.FC<EventTimelineProps> = ({
  eventTypes,
  profileName,
  showEventTypes = true,
  eventLogData,
  isLoading = false,
  selectedDate = dayjs()
}) => {
  const [hoveredEvent, setHoveredEvent] = useState<{
    label: string;
    stage: string;
    start: string;
    end: string;
    duration: string;
    isOngoing: boolean;
    color: string;
    x: number;
    y: number;
  } | null>(null);
  return (
    <div className="flex flex-col">
      {/* Event Title and Profile inside container */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <IconTitle icon={CalendarClock} title="Event Timeline" />
          {
            profileName && <div className="px-1.5 py-[5px] rounded bg-light-grey ml-2">
              <p className="paragraph-10 text-black leading-[10px]">{profileName}</p>
            </div>
          }
        </div>

        {showEventTypes && (
          <div className="flex items-center gap-2">
            {/* Event Types moved to the right */}
            <div className="flex gap-4">
              {eventTypes.map(type => (
                <div key={type.id} className="flex items-center gap-1">
                  <div className={cn("w-4 h-4 rounded flex items-center justify-center", type.color)}>
                    {type.icon}
                  </div>
                  <span className="paragraph-10 text-dark-grey">{type.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="relative"> {/* No margin - using parent flexbox gap */}
        <div className="bg-light-grey rounded-lg overflow-hidden relative mx-auto"
          style={{
            height: `${CONFIG.timeline.height * 0.25}rem`, // Convert to rem (h-6 = 1.5rem)
            maxWidth: `${CONFIG.timeline.width}%`,
            minWidth: CONFIG.timeline.minWidth > 0 ? `${CONFIG.timeline.minWidth}px` : undefined
          }}>
          {/* Map event types to time periods using actual API data */}
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex gap-1">
                <div className="w-1 h-1 bg-primary rounded-full animate-loading-dots"></div>
                <div className="w-1 h-1 bg-primary rounded-full animate-loading-dots delay-150"></div>
                <div className="w-1 h-1 bg-primary rounded-full animate-loading-dots delay-300"></div>
              </div>
            </div>
          ) : eventLogData && eventLogData.length > 0 ? (
            eventLogData.flatMap((event) => {
              // Find the matching event type based on blueprint name
              const eventType = eventTypes.find(type => 
                type.name.toLowerCase() === event.blueprint_name.toLowerCase()
              ) || eventTypes[0]; // Default to 'None' if not found

              // Map each stage to a timeline segment
              return event.stages.map((stage, stageIndex) => {
                const startTime = new Date(stage.started_at);
                // Handle ongoing events without end time - use current time
                const endTime = stage.ended_at ? new Date(stage.ended_at) : new Date();
                const isOngoing = !stage.ended_at;
                
                // Check if the dates are on the same day as the selected date
                const selectedDateStr = selectedDate.format('YYYY-MM-DD');
                const startDateStr = dayjs(startTime).format('YYYY-MM-DD');
                const endDateStr = dayjs(endTime).format('YYYY-MM-DD');
                
                // For ongoing events, always show if start date matches selected date
                // For completed events, skip if they don't occur on the selected date
                if (!isOngoing && startDateStr !== selectedDateStr && endDateStr !== selectedDateStr) {
                  return null;
                }
                if (isOngoing && startDateStr !== selectedDateStr) {
                  return null;
                }
                
                // Convert to local time
                let startHour = startTime.getHours();
                let startMinute = startTime.getMinutes();
                let endHour = endTime.getHours();
                let endMinute = endTime.getMinutes();
                
                // Handle events that start before the selected date
                if (startDateStr < selectedDateStr) {
                  startHour = 0;
                  startMinute = 0;
                }
                
                // Handle events that end after the selected date
                if (endDateStr > selectedDateStr) {
                  endHour = 23;
                  endMinute = 59;
                }
                
                const startPercent = ((startHour + startMinute / 60) / 24) * 100;
                const width = (((endHour + endMinute / 60) - (startHour + startMinute / 60)) / 24) * 100;
                
                // Calculate duration
                const durationMs = endTime.getTime() - startTime.getTime();
                const durationMinutes = Math.round(durationMs / (1000 * 60));
                const durationHours = Math.floor(durationMinutes / 60);
                const remainingMinutes = durationMinutes % 60;
                const durationStr = isOngoing 
                  ? 'Ongoing' 
                  : durationHours > 0 
                    ? `${durationHours}h ${remainingMinutes}m`
                    : `${remainingMinutes}m`;
                
                return {
                  id: `${event.automation_name}-${stage.stage}-${stageIndex}`,
                  type: eventType.id,
                  start: `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`,
                  end: `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`,
                  color: eventType.color,
                  label: event.automation_name,
                  blueprint: event.blueprint_name,
                  stage: stage.stage,
                  startPercent,
                  width,
                  duration: durationStr,
                  profileName: event.profile_name,
                  shadow: stage.shadow,
                  isOngoing,
                  startDateTime: startTime,
                  endDateTime: endTime,
                  metadata: stage.metadata
                };
              }).filter(Boolean); // Filter out null values
            }).map((item) => {
              // Skip items with invalid width (negative or zero)
              if (!item || item.width <= 0) return null;

            return (
              <div
                key={item.id}
                className={cn(
                  "absolute h-full transition-all duration-200 cursor-pointer",
                  item.color,
                  item.isOngoing && "animate-pulse", // Add pulsing animation for ongoing events
                  "hover:brightness-110 hover:shadow-md" // Subtle highlight on hover
                )}
                style={{
                  left: `${item.startPercent}%`,
                  width: `${item.width}%`
                }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoveredEvent({
                    label: item.label,
                    stage: item.stage,
                    start: item.start,
                    end: item.end,
                    duration: item.duration,
                    isOngoing: item.isOngoing,
                    color: item.color,
                    x: rect.left + rect.width / 2,
                    y: rect.top
                  });
                }}
                onMouseLeave={() => setHoveredEvent(null)}
              >
              </div>
            );
            })
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
              No events for selected date
            </div>
          )}
        </div>
        <div className="flex mx-auto"
          style={{
            maxWidth: `${CONFIG.timeline.width}%`,
            minWidth: CONFIG.timeline.minWidth > 0 ? `${CONFIG.timeline.minWidth}px` : undefined
          }}>
          {timelineData.map((time, i) => (
            <div key={i} className="flex-1 text-center">
              <div className="text-[10px] mb-0.5" style={{ color: '#666' }}>{time.time}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip rendered outside constrained containers */}
      {hoveredEvent && (
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: hoveredEvent.x,
            top: hoveredEvent.y - 8,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="bg-white border border-light-grey rounded-md py-2 px-3 shadow-lg min-w-[180px]">
            {/* Header with event name and status */}
            <div className="flex items-center justify-between gap-3 mb-1.5 pb-1.5 border-b border-light-grey">
              <div className="flex items-center gap-2">
                <div className={cn("h-2.5 w-2.5 rounded-full", hoveredEvent.color)} />
                <span className="text-xs font-semibold text-dark-blue">{hoveredEvent.label}</span>
              </div>
              {hoveredEvent.isOngoing && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-medium rounded-full">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </div>
            
            {/* Content */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-dark-grey">Stage</span>
                <span className="text-dark-blue font-medium">{hoveredEvent.stage}</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-dark-grey">Time</span>
                <span className="text-dark-blue">{hoveredEvent.start} - {hoveredEvent.isOngoing ? 'Now' : hoveredEvent.end}</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-dark-grey">Duration</span>
                <span className={cn(
                  "font-medium",
                  hoveredEvent.isOngoing ? "text-green-600" : "text-dark-blue"
                )}>
                  {hoveredEvent.duration}
                </span>
              </div>
            </div>
            
            {/* Arrow pointing down with border */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2">
              <svg width="12" height="6" viewBox="0 0 12 6" className="-mt-px">
                <path d="M0 0L6 6L12 0" fill="white" stroke="#E5E7EB" strokeWidth="1"/>
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
